import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DateSelector from '../Common/DateSelector';
import MetricCard from '../Common/MetricCard';
import ComboChart from '../Charts/ComboChart';
import MapView from '../Maps/MapView';
import DashboardHeader from '../Common/DashboardHeader';
import TelevisionTable from '../Tables/TelevisionTable';
import RadioTable from '../Tables/RadioTable';
import SportSignageTable from '../Tables/SportSignageTable';
import LazyLoad from '../Common/LazyLoad';
import { SkeletonChart, SkeletonTable } from '../Common/Skeletons';
import TabNavigation, { Tab } from '../Common/TabNavigation';
import UserManagementView from '../Admin/UserManagementView';
import CoverageAnalysisView from '../Admin/CoverageAnalysisView';
import { dataAPI, reportsAPI } from '../../services/api';
import { CHANNEL_COLORS } from '../../types';
import { hasChartData, hasTableData } from '../../utils/dataValidation';
import KnowledgePanel from '../Common/KnowledgePanel';
import { getDefinition } from '../../data/advertisingDefinitions';
import './DistrictManagerDashboard.css';

interface DealerData {
  name: string;
  distributor_id?: number;
  impressions: number;
  clicks: number;
  ctr: number;
  store_visits: number;
  trend: number[];
  zip?: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  state?: string;
  dma_code?: string;
  dma_name?: string;
  broadcast_tv_impressions?: number;
  radio_impressions?: number;
  channels?: Record<string, number>;   // Per-channel impression breakdown
}

interface DistrictMetrics {
  totalImpressions: number;
  digitalImpressions: number;
  broadcastImpressions: number;
  totalClicks: number;
  totalStoreVisits: number;
  avgCTR: number;
  dealerCount: number;
  trend?: number[];
}

interface CombinedMetrics {
  impressions: number;
  clicks: number;
  store_visits: number;
  ctr: number;
  trend?: number[];
}

interface ChannelChartData {
  display_chart: Array<{ date: string; impressions: number }>;
  dooh_chart: Array<{ date: string; impressions: number }>;
  streaming_tv_chart: Array<{ date: string; impressions: number }>;
  streaming_audio_chart: Array<{ date: string; impressions: number }>;
  youtube_chart: Array<{ date: string; impressions: number }>;
  search_chart: Array<{ date: string; impressions: number }>;
  broadcast_tv_chart: Array<{ date: string; impressions: number }>;
  radio_chart: Array<{ date: string; impressions: number }>;
  paid_social_chart: Array<{ date: string; impressions: number }>;
  sport_signage_chart: Array<{ date: string; impressions: number }>;
}

const DistrictManagerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedDealer, setSelectedDealer] = useState<string>('all');

  const getDefaultStartDate = () => {
    // Campaign started September 1, 2025 - always use this as default start date
    return new Date(2025, 8, 1); // September 1, 2025 (month 8 = September)
  };

  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: getDefaultStartDate().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [dealerData, setDealerData] = useState<DealerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const [channelChartData, setChannelChartData] = useState<ChannelChartData>({
    display_chart: [],
    dooh_chart: [],
    streaming_tv_chart: [],
    streaming_audio_chart: [],
    youtube_chart: [],
    search_chart: [],
    broadcast_tv_chart: [],
    radio_chart: [],
    paid_social_chart: [],
    sport_signage_chart: []
  });
  const [mapData, setMapData] = useState<any>({
    display: [],
    dooh: [],
    streaming_tv: [],
    youtube: [],
    search: []
  });
  const [dealerLocations, setDealerLocations] = useState<any[]>([]);
  const [spotsData, setSpotsData] = useState<any>(null);
  const [districtTotals, setDistrictTotals] = useState<{
    totalImpressions: number,
    digitalImpressions: number,
    broadcastImpressions: number,
    clicks: number
  } | null>(null);
  const [channelTotals, setChannelTotals] = useState<Record<string, number>>({});
  const [mlbSocialData, setMlbSocialData] = useState<{ views: number; engagement: number } | null>(null);
  const [knowledgePanelOpen, setKnowledgePanelOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState<string>('');

  const handleHelpClick = (topic: string) => {
    if (knowledgePanelOpen && currentTopic === topic) {
      setKnowledgePanelOpen(false);
    } else {
      setCurrentTopic(topic);
      setKnowledgePanelOpen(true);
    }
  };

  // Fetch dealer data for the district
  useEffect(() => {
    const fetchDealerData = async () => {
      if (!user?.username) {
        setError('User information not available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if viewing a specific district manager (from regional navigation)
        const districtManagerParam = (location.state as any)?.districtManager;

        console.log('[OPTIMIZATION #2] District: Using consolidated endpoint');
        const consolidatedStart = performance.now();

        // OPTIMIZATION #2: Single consolidated API call + MLB social (parallel)
        const [consolidated, socialData] = await Promise.all([
          reportsAPI.getConsolidatedDashboard(
            undefined, // No specific dealer - district view
            dateRange.start,
            dateRange.end,
            undefined, // No radius for district view
            districtManagerParam // Pass district manager parameter if viewing from regional
          ),
          reportsAPI.getMlbSocialSummary(dateRange.start, dateRange.end).catch(() => null)
        ]);
        if (socialData) setMlbSocialData({ views: socialData.views || 0, engagement: socialData.engagement || 0 });

        const consolidatedEnd = performance.now();
        console.log(`[OPTIMIZATION #2] District consolidated endpoint took ${(consolidatedEnd - consolidatedStart).toFixed(2)}ms`);
        console.log('District consolidated response:', consolidated);

        // Extract data from consolidated response
        const summary = consolidated.summary;
        const charts = consolidated.charts;
        const mapResponse = consolidated.map;
        const spotsData = consolidated.spots;

        console.log('[DEBUG] Summary object:', summary);
        console.log('[DEBUG] summary.dealers exists?', !!summary.dealers);
        console.log('[DEBUG] summary.dealers is array?', Array.isArray(summary.dealers));
        if (summary.dealers) {
          console.log('[DEBUG] summary.dealers length:', summary.dealers.length);
        }

        // Process dealer data from summary
        if (summary.dealers && Array.isArray(summary.dealers)) {
          console.log('[DEBUG] Processing dealers array with', summary.dealers.length, 'dealers');
          // Get dealer locations from dealer_locations API for coordinate data
          const districtId = (location.state as any)?.districtId || user.username?.replace(' ', '_').toLowerCase() || '';
          let dealerLocationsMap: Record<string, any> = {};

          try {
            const locationsResponse = await dataAPI.getDistrictDealers(districtId);
            const locationsList = locationsResponse.data || [];
            dealerLocationsMap = locationsList.reduce((acc: Record<string, any>, loc: any) => {
              acc[loc.name] = loc;
              return acc;
            }, {});
          } catch (err) {
            console.warn('Could not fetch dealer locations:', err);
          }

          const dealerData: DealerData[] = summary.dealers.map((dealer: any) => {
            const totalImpressions = dealer.impressions || 0;
            const totalClicks = dealer.clicks || 0;
            const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

            const locationData = dealerLocationsMap[dealer.dealer_name];
            const coords = locationData
              ? getCoordinatesForLocation(
                  locationData.ship_to_city || locationData.bill_to_city || '',
                  locationData.ship_to_state || locationData.bill_to_state || ''
                )
              : { latitude: 0, longitude: 0 };

            return {
              name: dealer.dealer_name,
              distributor_id: locationData ? parseInt(locationData.dealer_id) : dealer.dealer_id,
              impressions: totalImpressions,
              clicks: totalClicks,
              ctr,
              store_visits: dealer.store_visits || 0,
              trend: [],
              zip: locationData?.ship_to_zip || '',
              latitude: coords.latitude,
              longitude: coords.longitude,
              address: locationData?.ship_to_address_1 || locationData?.bill_to_address_1 || '',
              city: locationData?.ship_to_city || locationData?.bill_to_city || '',
              state: locationData?.ship_to_state || locationData?.bill_to_state || '',
              dma_code: dealer.dma_code || null,
              dma_name: dealer.dma_name || null,
              broadcast_tv_impressions: dealer.broadcast_tv_impressions || 0,
              radio_impressions: dealer.radio_impressions || 0,
              channels: dealer.channels
                ? Object.fromEntries(Object.entries(dealer.channels).map(([k, v]: [string, any]) => [k, v?.impressions ?? v ?? 0]))
                : undefined
            };
          });

          setDealerData(dealerData);

          // Store district-level TRUE totals (unique impressions, not sum of dealers)
          if (summary.total) {
            // Calculate three separate metrics
            let totalImpressions = 0;
            let digitalImpressions = 0;
            let broadcastImpressions = 0;
            let totalClicks = 0;

            Object.entries(summary.total).forEach(([channelName, channel]: [string, any]) => {
              const impressions = channel?.impressions || 0;
              const clicks = channel?.clicks || 0;

              // Add to total (all channels)
              totalImpressions += impressions;
              totalClicks += clicks;

              // Categorize into digital vs broadcast
              if (channelName === 'Broadcast TV' || channelName === 'Radio') {
                broadcastImpressions += impressions;
              } else {
                digitalImpressions += impressions;
              }
            });

            setDistrictTotals({
              totalImpressions,
              digitalImpressions,
              broadcastImpressions,
              clicks: totalClicks
            });

            // Store per-channel totals for individual metric cards
            const perChannel: Record<string, number> = {};
            Object.entries(summary.total).forEach(([channelName, channel]: [string, any]) => {
              perChannel[channelName] = channel?.impressions || 0;
            });
            setChannelTotals(perChannel);
          }

          // Set chart data from consolidated response
          setChannelChartData({
            display_chart: charts.Display || [],
            dooh_chart: charts.DOOH || [],
            streaming_tv_chart: charts['Streaming TV'] || [],
            streaming_audio_chart: charts['Streaming Audio'] || [],
            youtube_chart: charts.YouTube || [],
            search_chart: charts.Search || [],
            broadcast_tv_chart: charts['Broadcast TV'] || [],
            radio_chart: charts.Radio || [],
            paid_social_chart: charts['Paid Social'] || [],
            sport_signage_chart: charts['Sport Signage'] || []
          });

          // Transform map data for MapView component
          const transformedMapData: any = { display: [], dooh: [], streaming_tv: [] };

          if (mapResponse.zip_codes && Array.isArray(mapResponse.zip_codes)) {
            mapResponse.zip_codes.forEach((zipData: any) => {
              const coordinates: [number, number] | undefined =
                (zipData.longitude && zipData.latitude)
                  ? [zipData.longitude, zipData.latitude]
                  : undefined;

              if (zipData.channels && Array.isArray(zipData.channels)) {
                zipData.channels.forEach((channelData: any) => {
                  const channelKey = channelData.channel.toLowerCase().replace(' ', '_');

                  if (transformedMapData[channelKey]) {
                    transformedMapData[channelKey].push({
                      zip: zipData.zip_code,
                      impressions: channelData.impressions || 0,
                      coordinates: coordinates,
                      channels: zipData.channels,
                      city: zipData.city,
                      state: zipData.state
                    });
                  }
                });
              }
            });
          }

          setMapData(transformedMapData);

          // Set TV/Radio spots data from consolidated response
          setSpotsData(spotsData);
          console.log('[DISTRICT] Spots data:', spotsData);

          // Set dealer locations for map markers
          if (mapResponse.dealer_locations && Array.isArray(mapResponse.dealer_locations)) {
            setDealerLocations(mapResponse.dealer_locations.map((loc: any) => ({
              dealer_id: loc.dealer_id,
              name: loc.name,
              latitude: loc.latitude,
              longitude: loc.longitude,
              address: loc.address,
              city: loc.city,
              state: loc.state,
              zip: loc.zip
            })));
          } else {
            setDealerLocations([]);
          }
        } else {
          setDealerData([]);
        }

        console.log('[OPTIMIZATION #2] District dashboard loaded successfully from consolidated endpoint');
      } catch (err) {
        console.error('Error fetching dealer data:', err);
        setError('Failed to load dealer data');
      } finally {
        setLoading(false);
      }
    };

    if (dateRange.start && dateRange.end) {
      fetchDealerData();
    }
  }, [user, dateRange, location.state]);

  // Calculate district-wide metrics
  const districtMetrics = useMemo<DistrictMetrics>(() => {
    if (dealerData.length === 0) {
      return {
        totalImpressions: 0,
        digitalImpressions: 0,
        broadcastImpressions: 0,
        totalClicks: 0,
        totalStoreVisits: 0,
        avgCTR: 0,
        dealerCount: 0,
        trend: Array.from({ length: 7 }, () => 0)
      };
    }

    // Use TRUE district totals from backend (unique impressions, not sum of dealer overlaps)
    const totalImpressions = districtTotals?.totalImpressions || 0;
    const digitalImpressions = districtTotals?.digitalImpressions || 0;
    const broadcastImpressions = districtTotals?.broadcastImpressions || 0;
    const clicks = districtTotals?.clicks || 0;

    // Store visits still needs to be summed from dealers (not in district total)
    const storeVisits = dealerData.reduce((sum, dealer) => sum + dealer.store_visits, 0);

    // Calculate aggregate trend data (sum all dealer trends for district total)
    const aggregateTrend = Array.from({ length: 7 }, (_, index) => {
      return dealerData.reduce((sum, dealer) => {
        return sum + (dealer.trend?.[index] || 0);
      }, 0);
    });

    return {
      totalImpressions,
      digitalImpressions,
      broadcastImpressions,
      totalClicks: clicks,
      totalStoreVisits: storeVisits,
      avgCTR: digitalImpressions > 0 ? (clicks / digitalImpressions) * 100 : 0,
      dealerCount: dealerData.length,
      trend: aggregateTrend
    };
  }, [dealerData, districtTotals]);

  // Get data for selected dealer or all dealers
  const selectedData = useMemo<CombinedMetrics>(() => {
    if (selectedDealer === 'all') {
      return {
        impressions: districtMetrics.totalImpressions,
        clicks: districtMetrics.totalClicks,
        store_visits: districtMetrics.totalStoreVisits,
        ctr: districtMetrics.avgCTR,
        trend: districtMetrics.trend
      };
    }
    const dealer = dealerData.find(d => d.name === selectedDealer);
    if (dealer) {
      return {
        impressions: dealer.impressions,
        clicks: dealer.clicks,
        store_visits: dealer.store_visits,
        ctr: dealer.ctr,
        trend: dealer.trend
      };
    }
    return {
      impressions: 0,
      clicks: 0,
      store_visits: 0,
      ctr: 0,
      trend: []
    };
  }, [selectedDealer, dealerData, districtMetrics]);

  // Navigate to dealer dashboard
  const navigateToDealer = useCallback((dealer: DealerData) => {
    // Use dealer_id (the string like "78903549") instead of name
    // This is the MongoDB dealer_id field that backend queries use
    const dealerId = dealer.distributor_id ? String(dealer.distributor_id) : dealer.name;

    navigate('/dashboard', {
      state: {
        selectedDistributor: {
          distributor_id: dealerId,  // The actual dealer_id string for MongoDB queries
          dealer_id: dealerId,        // Also set dealer_id for consistency
          name: dealer.name,
          address: dealer.address,
          city: dealer.city,
          state: dealer.state,
          zip: dealer.zip,
          email: ''
        },
        fromDistrict: true,
        districtContext: {
          districtManager: location.state?.districtManager || user?.manager_name || user?.username,
          districtName: location.state?.districtName,
          regionContext: location.state?.regionContext
        }
      }
    });
  }, [navigate, location.state, user]);

  // Handle dealer selection
  const handleDealerSelect = useCallback((dealer: string) => {
    setSelectedDealer(dealer);
  }, []);

  // Format number with commas
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };


  // Simple geocoding function to get approximate coordinates
  const getCoordinatesForLocation = (city: string, state: string): { latitude: number; longitude: number } => {
    // Simple mapping of states to approximate center coordinates
    const stateCoords: Record<string, { lat: number; lng: number }> = {
      'AL': { lat: 32.7, lng: -86.8 }, 'AK': { lat: 64.1, lng: -153.0 }, 'AZ': { lat: 34.3, lng: -111.7 },
      'AR': { lat: 34.9, lng: -92.4 }, 'CA': { lat: 36.8, lng: -119.4 }, 'CO': { lat: 39.1, lng: -105.4 },
      'CT': { lat: 41.6, lng: -72.7 }, 'DE': { lat: 39.3, lng: -75.5 }, 'FL': { lat: 27.8, lng: -81.7 },
      'GA': { lat: 33.0, lng: -83.4 }, 'HI': { lat: 21.1, lng: -157.8 }, 'ID': { lat: 44.2, lng: -114.5 },
      'IL': { lat: 40.3, lng: -89.0 }, 'IN': { lat: 39.8, lng: -86.3 }, 'IA': { lat: 42.0, lng: -93.2 },
      'KS': { lat: 38.5, lng: -96.7 }, 'KY': { lat: 37.7, lng: -84.9 }, 'LA': { lat: 31.1, lng: -91.8 },
      'ME': { lat: 44.3, lng: -69.8 }, 'MD': { lat: 39.0, lng: -76.8 }, 'MA': { lat: 42.2, lng: -71.5 },
      'MI': { lat: 43.3, lng: -84.5 }, 'MN': { lat: 45.7, lng: -93.9 }, 'MS': { lat: 32.7, lng: -89.7 },
      'MO': { lat: 38.4, lng: -92.2 }, 'MT': { lat: 47.1, lng: -110.0 }, 'NE': { lat: 41.1, lng: -98.3 },
      'NV': { lat: 38.3, lng: -117.1 }, 'NH': { lat: 43.5, lng: -71.6 }, 'NJ': { lat: 40.3, lng: -74.5 },
      'NM': { lat: 34.8, lng: -106.2 }, 'NY': { lat: 42.2, lng: -74.9 }, 'NC': { lat: 35.6, lng: -79.8 },
      'ND': { lat: 47.5, lng: -99.8 }, 'OH': { lat: 40.4, lng: -82.8 }, 'OK': { lat: 35.6, lng: -96.9 },
      'OR': { lat: 44.6, lng: -122.0 }, 'PA': { lat: 40.6, lng: -77.2 }, 'RI': { lat: 41.7, lng: -71.5 },
      'SC': { lat: 33.8, lng: -80.9 }, 'SD': { lat: 44.3, lng: -99.4 }, 'TN': { lat: 35.7, lng: -86.9 },
      'TX': { lat: 31.1, lng: -97.6 }, 'UT': { lat: 40.2, lng: -111.5 }, 'VT': { lat: 44.0, lng: -72.7 },
      'VA': { lat: 37.8, lng: -78.2 }, 'WA': { lat: 47.4, lng: -121.5 }, 'WV': { lat: 38.5, lng: -80.9 },
      'WI': { lat: 44.3, lng: -89.8 }, 'WY': { lat: 42.8, lng: -107.3 }
    };

    // Add small random offset for cities within same state
    const baseCoords = stateCoords[state] || { lat: 39.8, lng: -98.6 }; // Default to center US
    const randomOffset = () => (Math.random() - 0.5) * 2; // +/- 1 degree

    return {
      latitude: baseCoords.lat + randomOffset(),
      longitude: baseCoords.lng + randomOffset()
    };
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading district data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="district-dashboard error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  // Define tabs for navigation
  const tabs: Tab[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'dashboard',
      content: (
        <div className="dashboard-container district-dashboard">
      <div className="dashboard-controls">
        <div className="controls-wrapper">
          <div className="filter-controls">
            <DateSelector 
              startDate={dateRange.start ? new Date(dateRange.start) : new Date()}
              endDate={dateRange.end ? new Date(dateRange.end) : new Date()}
              onDateChange={(start: Date, end: Date) => {
                setDateRange({
                  start: start.toISOString().split('T')[0],
                  end: end.toISOString().split('T')[0]
                });
              }} 
            />
            <div className="dealer-selector">
              <select 
                value={selectedDealer} 
                onChange={(e) => handleDealerSelect(e.target.value)}
                className="dealer-dropdown"
              >
                <option value="all">All Dealers (District Total)</option>
                {dealerData.map((dealer, index) => (
                  <option key={`dealer-option-${index}`} value={dealer.name}>
                    {dealer.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="view-toggle">
              <button 
                className={viewMode === 'table' ? 'active' : ''}
                onClick={() => setViewMode('table')}
              >
                Table View
              </button>
              <button 
                className={viewMode === 'map' ? 'active' : ''}
                onClick={() => setViewMode('map')}
              >
                Map View
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">
          <div className="metrics-grid">
            {(channelTotals['Display'] > 0) && (
              <MetricCard title="Display" value={channelTotals['Display']} color={CHANNEL_COLORS.display} onHelpClick={() => handleHelpClick('display')} />
            )}
            {(channelTotals['DOOH'] > 0) && (
              <MetricCard title="DOOH" value={channelTotals['DOOH']} color={CHANNEL_COLORS.dooh} hideClicks={true} onHelpClick={() => handleHelpClick('dooh')} />
            )}
            {(channelTotals['Streaming TV'] > 0) && (
              <MetricCard title="Streaming TV" value={channelTotals['Streaming TV']} color={CHANNEL_COLORS.streaming_tv} hideClicks={true} onHelpClick={() => handleHelpClick('streaming_tv')} />
            )}
            {(channelTotals['Broadcast TV'] > 0) && (
              <MetricCard title="Broadcast TV" value={channelTotals['Broadcast TV']} color={CHANNEL_COLORS.broadcast_tv} hideClicks={true} onHelpClick={() => handleHelpClick('broadcast_tv')} />
            )}
            {(spotsData?.summary?.sport_signage_impressions > 0) && (
              <MetricCard title="Sport Signage – MLB & NCAA" value={spotsData.summary.sport_signage_impressions} color={CHANNEL_COLORS.sport_signage} hideClicks={true} onHelpClick={() => handleHelpClick('sport_signage')} />
            )}
            {(channelTotals['YouTube'] > 0) && (
              <MetricCard title="YouTube" value={channelTotals['YouTube']} color={CHANNEL_COLORS.youtube} onHelpClick={() => handleHelpClick('youtube')} />
            )}
            {(channelTotals['Paid Social'] > 0) && (
              <MetricCard title="Paid Social" value={channelTotals['Paid Social']} color={CHANNEL_COLORS.paid_social} onHelpClick={() => handleHelpClick('paid_social')} />
            )}
            {(channelTotals['Streaming Audio'] > 0) && (
              <MetricCard title="Streaming Audio" value={channelTotals['Streaming Audio']} color={CHANNEL_COLORS.streaming_audio} hideClicks={true} onHelpClick={() => handleHelpClick('streaming_audio')} />
            )}
            {(channelTotals['Radio'] > 0) && (
              <MetricCard title="Radio" value={channelTotals['Radio']} color={CHANNEL_COLORS.radio} hideClicks={true} onHelpClick={() => handleHelpClick('radio')} />
            )}
            {(selectedData.clicks > 0) && (
              <MetricCard
                title="Total Clicks"
                value={selectedData.clicks || 0}
                icon={<i className="material-icons">touch_app</i>}
                onHelpClick={() => handleHelpClick('total_clicks')}
              />
            )}
            {(selectedData.store_visits > 0) && (
              <MetricCard
                title="Store Visits"
                value={selectedData.store_visits || 0}
                icon={<i className="material-icons">store</i>}
                onHelpClick={() => handleHelpClick('store_visits')}
              />
            )}
            {(selectedData.clicks > 0 || districtMetrics.digitalImpressions > 0) && (
              <MetricCard
                title="Avg CTR"
                value={selectedData.ctr || 0}
                icon={<i className="material-icons">percent</i>}
                onHelpClick={() => handleHelpClick('avg_ctr')}
              />
            )}
            {mlbSocialData && mlbSocialData.views > 0 && (
              <MetricCard title="MLB National Social – Views" value={mlbSocialData.views} color="#1877F2" hideClicks={true} onHelpClick={() => handleHelpClick('mlb_social_views')} />
            )}
            {mlbSocialData && mlbSocialData.engagement > 0 && (
              <MetricCard title="MLB National Social – Engagement" value={mlbSocialData.engagement} color="#1DA1F2" hideClicks={true} onHelpClick={() => handleHelpClick('mlb_social_engagement')} />
            )}
          </div>

          {viewMode === 'table' && dealerData.length > 0 ? (
            <div className="data-table-container">
              <h2>Dealer Performance</h2>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Dealer Name</th>
                      <th>Location</th>
                      <th>DMA</th>
                      <th>Display</th>
                      <th>DOOH</th>
                      <th>Streaming TV</th>
                      <th>Streaming Audio</th>
                      <th>Broadcast TV</th>
                      <th>Radio</th>
                      <th>Sport Signage</th>
                      <th>Store Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealerData.map((dealer, index) => {
                      const ch = dealer.channels || {};
                      const fmt = (v: number | undefined) => v && v > 0
                        ? <span>{formatNumber(v)}</span>
                        : <span style={{ color: '#999' }}>—</span>;
                      return (
                      <tr key={`dealer-row-${index}`} className={selectedDealer === dealer.name ? 'selected' : ''}>
                        <td className="dealer-name">
                          <div className="label-with-action">
                            <span>{dealer.name}</span>
                            <button className="table-action-btn" onClick={() => navigateToDealer(dealer)}>View Dealer</button>
                          </div>
                        </td>
                        <td>
                          {dealer.city && dealer.city.trim()
                            ? `${dealer.city}, ${dealer.state}`
                            : dealer.state || 'N/A'
                          }
                        </td>
                        <td>
                          {dealer.dma_code ? (
                            <span title={dealer.dma_name || 'Unknown DMA'} style={{ cursor: 'help' }}>
                              {dealer.dma_code}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>N/A</span>
                          )}
                        </td>
                        <td>{fmt(ch['Display'])}</td>
                        <td>{fmt(ch['DOOH'])}</td>
                        <td>{fmt(ch['Streaming TV'])}</td>
                        <td>{fmt(ch['Streaming Audio'])}</td>
                        <td>{fmt(ch['Broadcast TV'])}</td>
                        <td>{fmt(ch['Radio'])}</td>
                        <td>{fmt(ch['Sport Signage'])}</td>
                        <td>{dealer.store_visits > 0 ? <span>{formatNumber(dealer.store_visits)}</span> : <span style={{ color: '#999' }}>—</span>}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="map-container">
              <h2>District Coverage Map</h2>
              <MapView
                data={mapData}
                viewLevel="district"
                dealerLocations={dealerLocations}
              />
            </div>
          )}

          <LazyLoad fallback={<div className="chart-placeholder">Loading charts...</div>}>
            <section className="charts-section">
              <div className="charts-grid">
                {loading ? (
                  <>
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                    <SkeletonChart height={300} />
                  </>
                ) : (
                  <>
                    {hasChartData(channelChartData.display_chart) && (
                      <ComboChart
                        data={channelChartData.display_chart}
                        title="Display Performance"
                        color={CHANNEL_COLORS.display}
                        height={300}
                      />
                    )}
                    {hasChartData(channelChartData.dooh_chart) && (
                      <ComboChart
                        data={channelChartData.dooh_chart}
                        title="DOOH Performance"
                        color={CHANNEL_COLORS.dooh}
                        height={300}
                        showClicks={false}
                      />
                    )}
                    {hasChartData(channelChartData.streaming_tv_chart) && (
                      <ComboChart
                        data={channelChartData.streaming_tv_chart}
                        title="Streaming TV Performance"
                        color={CHANNEL_COLORS.streaming_tv}
                        height={300}
                        showClicks={false}
                      />
                    )}
                    {hasChartData(channelChartData.youtube_chart) && (
                      <ComboChart
                        data={channelChartData.youtube_chart}
                        title="YouTube Performance"
                        color={CHANNEL_COLORS.youtube}
                        height={300}
                      />
                    )}
                    {hasChartData(channelChartData.search_chart) && (
                      <ComboChart
                        data={channelChartData.search_chart}
                        title="Search Performance"
                        color={CHANNEL_COLORS.search}
                        height={300}
                      />
                    )}
                    {hasChartData(channelChartData.broadcast_tv_chart) && (
                      <ComboChart
                        data={channelChartData.broadcast_tv_chart}
                        title="Broadcast TV Performance"
                        color={CHANNEL_COLORS.broadcast_tv}
                        height={300}
                        showClicks={false}
                      />
                    )}
                    {hasChartData(channelChartData.sport_signage_chart) && (
                      <ComboChart
                        data={channelChartData.sport_signage_chart}
                        title="Sport Signage Performance"
                        color={CHANNEL_COLORS.sport_signage}
                        height={300}
                        showClicks={false}
                      />
                    )}
                    {hasChartData(channelChartData.streaming_audio_chart) && (
                      <ComboChart
                        data={channelChartData.streaming_audio_chart}
                        title="Streaming Audio Performance"
                        color={CHANNEL_COLORS.streaming_audio}
                        height={300}
                        showClicks={false}
                      />
                    )}
                    {hasChartData(channelChartData.radio_chart) && (
                      <ComboChart
                        data={channelChartData.radio_chart}
                        title="Radio Performance"
                        color={CHANNEL_COLORS.radio}
                        height={300}
                        showClicks={false}
                      />
                    )}
                    {hasChartData(channelChartData.paid_social_chart) && (
                      <ComboChart
                        data={channelChartData.paid_social_chart}
                        title="Paid Social Performance"
                        color={CHANNEL_COLORS.paid_social}
                        height={300}
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          </LazyLoad>

          {/* TV/Radio Spots Tables */}
          {loading ? (
            <>
              <section className="television-section">
                <SkeletonTable columns={7} rows={5} />
              </section>
              <section className="radio-section">
                <SkeletonTable columns={7} rows={5} />
              </section>
            </>
          ) : spotsData && (
            <LazyLoad fallback={<div className="lazy-placeholder">Loading tables...</div>}>
              {hasTableData(spotsData.television) && (
                <section className="television-section">
                  <TelevisionTable
                    data={spotsData.television || []}
                    onHelpClick={() => handleHelpClick('broadcast_tv')}
                  />
                  <div className="dma-info" style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                    Showing TV spots for {spotsData.summary?.unique_dmas || 0} unique DMAs
                  </div>
                </section>
              )}

              {hasTableData(spotsData.radio) && (
                <section className="radio-section">
                  <RadioTable
                    data={spotsData.radio || []}
                    onHelpClick={() => handleHelpClick('radio')}
                  />
                </section>
              )}

              {hasTableData(spotsData.sport_signage) && (
                <section className="sport-signage-section">
                  <SportSignageTable
                    data={spotsData.sport_signage || []}
                    onHelpClick={() => handleHelpClick('sport_signage')}
                  />
                </section>
              )}
            </LazyLoad>
          )}

        </div>
      </div>
    </div>
      )
    },
    {
      id: 'dealers',
      label: 'My Dealers',
      icon: 'people',
      content: (
        <UserManagementView
          currentUserRole={user?.role}
          scopeMessage={`Showing dealers managed by ${user?.manager_name || 'you'} (automatically filtered by backend)`}
        />
      )
    },
    {
      id: 'coverage',
      label: 'Coverage Analysis',
      icon: 'analytics',
      content: (
        <CoverageAnalysisView
          hierarchicalScope="district"
          managerName={user?.manager_name}
          scopeMessage={`Showing coverage analysis for ${user?.manager_name || 'your'} district dealers`}
        />
      )
    }
  ];

  return (
    <div className="district-manager-dashboard-wrapper">
      <KnowledgePanel
        isOpen={knowledgePanelOpen}
        onClose={() => setKnowledgePanelOpen(false)}
        topic={currentTopic}
        title={getDefinition(currentTopic)?.title || ''}
        description={getDefinition(currentTopic)?.description || ''}
      />
      <DashboardHeader
        title="District Manager Dashboard"
        subtitle={location.state?.districtManager || user?.manager_name || user?.username}
        badges={[
          { label: `${districtMetrics.dealerCount} Dealers`, variant: 'primary' }
        ]}
      />
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </div>
  );
};

export default React.memo(DistrictManagerDashboard);
